/**
 * The single "is this inbound message still live and unanswered" contract
 * for `os_linkedin_drafts`, shared by every consumer that needs to answer
 * that question — the Reply Queue tab (`src/hooks/useReplyQueue.ts`) and the
 * Lead Dossier (`src/hooks/useLeadDossier.ts`). Before this module existed,
 * the dossier reimplemented the predicate independently and silently
 * dropped the non-empty-message rule, letting it claim a live queue item
 * the queue itself correctly excludes.
 *
 * Matches the queue's exact SQL semantics:
 *   status IN ('awaiting_draft', 'pending_approval')
 *   AND is_superseded IS NOT TRUE
 *   AND incoming_message <> ''
 *
 * `incoming_message` is `NOT NULL` in the schema
 * (supabase/migrations/001_tacit_initial_schema.sql:261) but has no
 * non-empty CHECK constraint — legacy rows, imports, and service-role
 * writers can still produce `''`, so the non-empty rule is load-bearing,
 * not defensive decoration.
 */

export const LIVE_DRAFT_STATUSES = ['awaiting_draft', 'pending_approval'] as const;

export type LiveDraftStatus = (typeof LIVE_DRAFT_STATUSES)[number];

export interface LiveDraftCandidate {
  /** `string | null` because the generated DB Row type allows NULL here
   * (no application write path produces it, but the predicate must not
   * assume that) — a null status is never live. */
  status: string | null;
  is_superseded: boolean | null;
  incoming_message: string;
}

const LIVE_DRAFT_STATUS_SET: ReadonlySet<string> = new Set(LIVE_DRAFT_STATUSES);

export function isLiveDraftStatus(status: string | null): status is LiveDraftStatus {
  return status !== null && LIVE_DRAFT_STATUS_SET.has(status);
}

export function isLiveDraft(row: LiveDraftCandidate): boolean {
  return (
    isLiveDraftStatus(row.status) &&
    row.is_superseded !== true &&
    row.incoming_message !== ''
  );
}

/**
 * The single "which draft wins" total order, shared by every consumer that
 * has to pick ONE draft among several for the same lead — the queue's
 * dedupe (one row per `heyreach_lead_id`) and the dossier's `livePendingDraft`
 * pick. Before this existed the two independently derived "latest" and
 * could disagree on a tied `incoming_at` (the webhook writes the provider's
 * `sentAt` verbatim with no uniqueness constraint on
 * `(heyreach_lead_id, incoming_at)` — two campaigns/messages for the same
 * lead can land on the identical timestamp).
 *
 * More recent `incoming_at` wins; a tie breaks on the lexicographically
 * smaller `id` — matching the queue's own keyset pagination total order
 * (`incoming_at DESC, id ASC`).
 */
export interface DraftRecency {
  incoming_at: string;
  id: string;
}

export function compareDraftRecency(a: DraftRecency, b: DraftRecency): number {
  const diff = new Date(b.incoming_at).getTime() - new Date(a.incoming_at).getTime();
  if (diff !== 0) return diff;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

/** The winning draft among `candidates`, per `compareDraftRecency` — or
 * `null` for an empty list. */
export function pickMostRecentDraft<T extends DraftRecency>(candidates: readonly T[]): T | null {
  if (candidates.length === 0) return null;
  return [...candidates].sort(compareDraftRecency)[0] ?? null;
}
